import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { generateWebPageSchema, generateBreadcrumbSchema } from "@/components/SEOHead";
import HelmetSEO from "@/components/HelmetSEO";
import { MessageCircle, Phone, Send, MapPin, Clock, Eye, ChevronLeft, ChevronRight, Star, Calendar, X } from "lucide-react";

const AdDetail = () => {
  const { adId } = useParams();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [freeAdsAsTop, setFreeAdsAsTop] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAd();
    loadFreeAdsAsTopSetting();
  }, [adId]);

  const seoCanonical = (() => {
    if (!ad) return null;
    if (ad.slug && ad.category_slug && ad.city_slug) {
      return `https://uplino.in/${ad.city_slug}/${ad.category_slug}/${ad.slug}`;
    }
    return `https://uplino.in/ad/${adId}`;
  })();

  const isExpiredOrInactive = (() => {
    if (!ad) return false;
    if (ad.status && ad.status !== "approved") return true;
    if (ad.expires_at && typeof ad.expires_at === "string") {
      return new Date(ad.expires_at).getTime() < Date.now();
    }
    return false;
  })();

  const seoTitle = ad ? `${ad.title} | uplino.in` : "Loading... | uplino.in";
  const seoDescription = ad
    ? (ad.description || "").replace(/\s+/g, " ").trim().slice(0, 155)
    : "";

  // Auto slideshow
  useEffect(() => {
    if (ad?.images?.length > 1 && !isFullscreen) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [ad, isFullscreen]);

  const loadFreeAdsAsTopSetting = async () => {
    try {
      const response = await api.get("/settings/free-ads-as-top");
      setFreeAdsAsTop(response.data.free_ads_as_top || false);
    } catch (error) {
      console.error("Failed to load free ads as top setting:", error);
    }
  };

  const loadAd = async () => {
    try {
      const response = await api.get(`/ads/${adId}`);
      setAd(response.data);
    } catch (error) {
      toast.error("Failed to load ad");
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const trackClick = async (eventType) => {
    try {
      await api.post("/analytics/track", {
        ad_id: adId,
        event_type: eventType,
      });
    } catch (error) {
      console.error("Failed to track click");
    }
  };

  const handleWhatsAppClick = () => {
    trackClick("whatsapp_click");
    const message = encodeURIComponent(`Hi, I saw your ad "${ad.title}" on uplino.in and I'm interested.`);
    window.open(`https://wa.me/${ad.contact_whatsapp}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    trackClick("call_click");
    window.location.href = `tel:${ad.contact_phone}`;
  };

  const handleTelegramClick = () => {
    trackClick("telegram_click");
    // For Telegram, we can use start parameter with bot or just open the chat
    const telegramId = ad.contact_telegram.replace(/[^0-9a-zA-Z_]/g, '');
    // Check if it's a username or phone number
    const isUsername = isNaN(telegramId);
    if (isUsername) {
      window.open(`https://t.me/${telegramId}`, '_blank');
    } else {
      window.open(`https://t.me/+${telegramId}`, '_blank');
    }
  };

  const nextImage = useCallback(() => {
    if (ad?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % ad.images.length);
    }
  }, [ad]);

  const prevImage = useCallback(() => {
    if (ad?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + ad.images.length) % ad.images.length);
    }
  }, [ad]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (!ad) {
    return null;
  }

  const schema = ad
    ? {
        "@context": "https://schema.org",
        "@graph": [
          generateWebPageSchema(seoTitle, seoDescription, seoCanonical),
          generateBreadcrumbSchema([
            { name: "Home", url: "https://uplino.in" },
            { name: "Ad", url: seoCanonical },
          ]),
        ],
      }
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <HelmetSEO title={seoTitle} description={seoDescription} canonical={seoCanonical} noIndex={isExpiredOrInactive} />
      <SEOHead title={seoTitle} description={seoDescription} canonical={seoCanonical} schema={schema} noIndex={isExpiredOrInactive} />
      <Header />

      {/* Fullscreen Image Modal */}
      {isFullscreen && ad.images && ad.images.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <Button
            variant="ghost"
            className="absolute top-4 right-4 text-white hover:bg-white/10 z-10"
            onClick={() => setIsFullscreen(false)}
          >
            <X className="w-8 h-8" />
          </Button>
          
          <Button
            variant="ghost"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
            onClick={prevImage}
          >
            <ChevronLeft className="w-10 h-10" />
          </Button>
          
          <img
            src={ad.images[currentImageIndex]}
            alt={`${ad.title} - Photo ${currentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />
          
          <Button
            variant="ghost"
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10 z-10"
            onClick={nextImage}
          >
            <ChevronRight className="w-10 h-10" />
          </Button>
          
          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm">
            {currentImageIndex + 1} / {ad.images.length}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        <Card className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border-white/10 overflow-hidden shadow-2xl">
          {/* Premium Image Slideshow Section */}
          {ad.images && ad.images.length > 0 ? (
            <div className="relative bg-black">
              {/* Main Image - Full Size 9:16 Ratio */}
              <div 
                className="relative aspect-[9/16] sm:aspect-[3/4] md:aspect-[4/5] lg:aspect-[3/4] max-h-[70vh] overflow-hidden cursor-pointer"
                onClick={() => setIsFullscreen(true)}
              >
                <img
                  src={ad.images[currentImageIndex]}
                  alt={`${ad.title} - Photo ${currentImageIndex + 1}`}
                  className="w-full h-full object-cover transition-all duration-500"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                
                {/* Navigation Arrows */}
                {ad.images.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm"
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm"
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                    >
                      <ChevronRight className="w-6 h-6" />
                    </Button>
                  </>
                )}
                
                {/* Image Counter Badge */}
                <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-medium backdrop-blur-sm">
                  {currentImageIndex + 1} / {ad.images.length}
                </div>
                
                {/* Featured Badge */}
                {ad.is_global_pinned && (
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1 shadow-lg">
                    <Star className="w-4 h-4" /> Featured
                  </div>
                )}
                
                {/* Click to expand hint */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white/80 px-4 py-2 rounded-full text-xs backdrop-blur-sm">
                  Click to view fullscreen
                </div>
              </div>
              
              {/* Thumbnail Strip */}
              {ad.images.length > 1 && (
                <div className="flex gap-2 p-3 bg-slate-900/90 overflow-x-auto">
                  {ad.images.map((img, index) => (
                    <div
                      key={index}
                      className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'ring-2 ring-pink-500 scale-105' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="aspect-[3/4] bg-gradient-to-br from-pink-600/20 to-purple-600/20 flex items-center justify-center">
              <span className="text-9xl">📸</span>
            </div>
          )}

          {/* Content Section */}
          <div className="p-6 sm:p-8">
            {/* Title & Meta */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{fontFamily: 'Inter, sans-serif'}}>
                {ad.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                  <Eye className="w-4 h-4 text-pink-400" />
                  {ad.views || 0} views
                </span>
                {ad.age && (
                  <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    {ad.age} years
                  </span>
                )}
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                  <Clock className="w-4 h-4 text-blue-400" />
                  {ad.time_slots?.join(', ') || 'Available'}
                </span>
                <span className="flex items-center gap-1.5 bg-white/5 px-3 py-1.5 rounded-full">
                  <MapPin className="w-4 h-4 text-green-400" />
                  {ad.is_global_pinned ? "India" : (ad.city_names?.[0] || 'India')}
                </span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-3">Description</h2>
              <div className="bg-white/5 rounded-xl p-4 sm:p-6">
                <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{ad.description}</p>
              </div>
            </div>

            {/* Contact Buttons - Premium Style */}
            {/* Show contact buttons if: ad is paid OR freeAdsAsTop is enabled */}
            {(!ad.is_free || freeAdsAsTop) ? (
              <div className="space-y-3">
                {ad.contact_phone && (
                  <Button
                    data-testid="ad-call-button"
                    onClick={handleCallClick}
                    className="w-full h-14 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-pink-500/30 transition-all duration-300 hover:scale-[1.01]"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Call Now
                  </Button>
                )}

                {ad.contact_whatsapp && (
                  <Button
                    data-testid="ad-whatsapp-button"
                    onClick={handleWhatsAppClick}
                    className="w-full h-14 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-green-500/30 transition-all duration-300 hover:scale-[1.01]"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    WhatsApp Message
                  </Button>
                )}

                {ad.contact_telegram && (
                  <Button
                    data-testid="ad-telegram-button"
                    onClick={handleTelegramClick}
                    className="w-full h-14 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg shadow-blue-500/30 transition-all duration-300 hover:scale-[1.01]"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Telegram Message
                  </Button>
                )}
              </div>
            ) : (
              /* Free ad without freeAdsAsTop enabled - show upgrade message */
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6 text-center">
                <div className="text-4xl mb-3">🔒</div>
                <h3 className="text-xl font-bold text-white mb-2">Contact Info Hidden</h3>
                <p className="text-gray-400 mb-4">This is a free listing. Contact information is not available.</p>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                >
                  Post Your Own Ad
                </Button>
              </div>
            )}

            {/* Verification Badges */}
            <div className="mt-6 flex flex-wrap gap-2">
              {ad.phone_verified && (
                <span className="px-3 py-1.5 bg-green-600/20 text-green-400 text-sm rounded-full border border-green-500/30">
                  ✓ Phone Verified
                </span>
              )}
              {ad.agent_verified && (
                <span className="px-3 py-1.5 bg-blue-600/20 text-blue-400 text-sm rounded-full border border-blue-500/30">
                  ✓ Agent Verified
                </span>
              )}
              {ad.admin_verified && (
                <span className="px-3 py-1.5 bg-purple-600/20 text-purple-400 text-sm rounded-full border border-purple-500/30">
                  ✓ Admin Verified
                </span>
              )}
            </div>
          </div>
        </Card>

        {/* Mobile Sticky Buttons - Only show if paid ad OR freeAdsAsTop enabled */}
        {(!ad.is_free || freeAdsAsTop) && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-950/95 backdrop-blur-md border-t border-white/10 md:hidden z-40">
            <div className="flex gap-2 max-w-4xl mx-auto">
              {ad.contact_phone && (
                <Button
                  onClick={handleCallClick}
                  className="flex-1 h-12 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white rounded-xl"
                >
                  <Phone className="w-5 h-5" />
                </Button>
              )}
              {ad.contact_whatsapp && (
                <Button
                  onClick={handleWhatsAppClick}
                  className="flex-1 h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl"
                >
                  <MessageCircle className="w-5 h-5" />
                </Button>
              )}
              {ad.contact_telegram && (
                <Button
                  onClick={handleTelegramClick}
                  className="flex-1 h-12 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl"
                >
                  <Send className="w-5 h-5" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="pb-20 md:pb-0">
        <Footer />
      </div>
    </div>
  );
};

export default AdDetail;
